import { DateTimePickerProps } from '@snowball-tech/fractal/dist/src/components/DateTimePicker'
import {
  type Formats,
  type IntlError,
  type TranslationValues,
  IntlErrorCode,
} from 'next-intl'
import { createNavigation } from 'next-intl/navigation'
import NextLink from 'next/link'

import isEmpty from 'lodash/fp/isEmpty'

import type { HTTPError, Translator } from '../types'

import routing from '../i18n/routing'
import { createLogger } from './log'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - I don'know how to type `Link` here and solve the ts(2742) error
export const {
  Link,
  redirect,
  usePathname,
  useRouter,
}: {
  Link: typeof NextLink
  redirect: ReturnType<typeof createNavigation>['redirect']
  usePathname: ReturnType<typeof createNavigation>['usePathname']
  useRouter: ReturnType<typeof createNavigation>['useRouter']
} = createNavigation(routing)

export function handleError(error: IntlError) {
  const logger = createLogger('handleTranslationError')

  const key = error.message.match(/`([^`]*)`/)?.[1] ?? error.message

  if (
    error.code === IntlErrorCode.MISSING_MESSAGE ||
    key === IntlErrorCode.MISSING_MESSAGE
  ) {
    if (
      error.stack?.includes('.notEmpty ') ||
      error.stack?.includes('IfNotEmpty') ||
      isEmpty(key) ||
      key === IntlErrorCode.MISSING_MESSAGE ||
      key.includes(' ')
    ) {
      return
    }

    logger.error(`[TRANSLATIONS] Missing key '${key}'`, {}, error)
  } else {
    logger.error(
      !isEmpty(key) && key !== IntlErrorCode.MISSING_MESSAGE
        ? `[TRANSLATIONS] Unable to translate key '${key}': "${error.message}"`
        : error.message,
      {},
      error,
    )
  }
}
export function translateIfNotEmpty(
  key: string | null | undefined,
  $t: Translator,
  values?: TranslationValues | undefined,
  formats?: Partial<Formats> | undefined,
) {
  if (isEmpty(key)) {
    return ''
  }

  const translatedContent = $t(key, values, formats)

  return translatedContent === key ? '' : translatedContent
}

export function translateWithFallback(
  key: string | null | undefined,
  fallback: string,
  $t: Translator,
  values?: TranslationValues | undefined,
  formats?: Partial<Formats> | undefined,
) {
  return translateIfNotEmpty(key, $t, values, formats) || fallback
}

/** @public */
export function replaceTranslationInline(string: string | null | undefined) {
  if (isEmpty(string) || !string.startsWith('$t(') || !string.endsWith(')')) {
    return string || ''
  }

  return string.replace('$t(', '').replace(')', '')
}

export function translateInline(
  string: string | null | undefined,
  $t: Translator,
  values?: TranslationValues | undefined,
  formats?: Partial<Formats> | undefined,
  fallback?: string | null | undefined,
) {
  if (isEmpty(string) || !string.startsWith('$t(') || !string.endsWith(')')) {
    return string
  }

  const toTranslate = replaceTranslationInline(string)

  return isEmpty(fallback)
    ? $t(toTranslate, values, formats)
    : translateWithFallback(toTranslate, fallback, $t, values, formats)
}

export function translateHTTPError(error: HTTPError, $t: Translator) {
  if (!isEmpty(error.message)) {
    return error.message
  }

  return `${error.status} - ${translateWithFallback(
    `general.http.error.${error.status}`,
    `${
      $t.rich('general.http.error.default', {
        message: isEmpty(error.statusText) ? '' : `: "${error.statusText}"`,
      }) as string
    }`,
    $t,
  )}`
}

export function getDateTimePickerTranslations(
  $t: Translator,
  override?: Partial<DateTimePickerProps['i18n']>,
) {
  return {
    buttons: {
      clear: $t('datetime_picker.clear'),
      close: $t('datetime_picker.close'),
      now: $t('datetime_picker.now'),
      today: $t('datetime_picker.today'),
      ...override?.buttons,
    },
    calendar: {
      next: $t('datetime_picker.next'),
      previous: $t('datetime_picker.previous'),
      ...override?.calendar,
    },
    tabs: {
      bar: $t('datetime_picker.bar'),
      date: $t('datetime_picker.date'),
      time: $t('datetime_picker.time'),
      ...override?.tabs,
    },
  }
}
